import { useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { endOfMonth, startOfMonth } from "date-fns";

import {
  TRACKED_PRODUCTS,
  useJourneyFunnel,
  useJourneys,
} from "@/app/hooks/useJourneys";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { onlyCpfDigits } from "@/app/utils/cpf";
import { JourneyStatusEnum } from "@/app/services/journeyService";

import {
  JourneyFilterValues,
  JourneySituation,
} from "./components/JourneyFilters";
import { TableReportJourneysColumnsDefinition } from "./components/TableReportJourneysDefinition";

const PAGE_SIZE = 10;

const initialFilters: JourneyFilterValues = {
  product: TRACKED_PRODUCTS[0],
  situation: "ALL",
  search: "",
  from: startOfMonth(new Date()).toISOString(),
  to: endOfMonth(new Date()).toISOString(),
};

const statusOf = (situation: JourneySituation) =>
  situation === "ALL" || situation === "ABANDONED"
    ? undefined
    : [situation as JourneyStatusEnum];

export const useReportJourneysController = () => {
  const [filters, setFilters] = useState<JourneyFilterValues>(initialFilters);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string>();

  const searchDigits = onlyCpfDigits(filters.search) || undefined;

  const { data, isFetching, isLoading, setParams } = useJourneys({
    product: [filters.product],
    from: filters.from,
    to: filters.to,
  });

  const { data: funnel, isFetching: isFetchingFunnel } = useJourneyFunnel({
    product: filters.product,
    from: filters.from,
    to: filters.to,
    search: searchDigits,
  });

  const { getProductLabel } = useProductsCatalog();

  const { columns } = TableReportJourneysColumnsDefinition();

  const table = useReactTable({
    data: data?.journeys ?? [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit ?? PAGE_SIZE,
      },
    },
  });

  const handleApplyFilters = (next: JourneyFilterValues) => {
    setFilters(next);

    setParams((current) => ({
      ...current,
      product: [next.product],
      status: statusOf(next.situation),
      onlyStale: next.situation === "ABANDONED" || undefined,
      from: next.from || undefined,
      to: next.to || undefined,
      search: onlyCpfDigits(next.search) || undefined,
      skip: 0,
    }));
  };

  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? PAGE_SIZE;
  const skip = data?.pagination.skip ?? 0;

  const pageCount = Math.max(1, Math.ceil(total / limit));
  const pageIndex = Math.floor(skip / limit);

  const handleNextPage = () => {
    if (pageIndex + 1 >= pageCount) return;

    setParams((current) => ({ ...current, skip: skip + limit }));
  };

  const handlePreviousPage = () => {
    if (skip <= 0) return;

    setParams((current) => ({ ...current, skip: Math.max(0, skip - limit) }));
  };

  return {
    table,
    funnel,
    isFetching,
    isLoading,
    isFetchingFunnel,
    filters,
    products: TRACKED_PRODUCTS,
    getProductLabel,
    selectedJourneyId,
    setSelectedJourneyId,
    handleApplyFilters,
    getPageCount: () => pageCount,
    getPageIndex: () => pageIndex,
    handleNextPage,
    handlePreviousPage,
  };
};
